// src/components/Video/VideoItem.tsx
import React from 'react';
import { Link } from 'react-router-dom';
import { Card, Stack, Image, Badge, Text, Anchor } from '@mantine/core';
import Countdown, { type CountdownRenderProps } from 'react-countdown';
import classes from './VideoItem.module.scss';
import type { VideoItemData } from '@/types/video';
import { timeAgo } from '@/utils';

type Props = {
    item?: VideoItemData | null;               // допускаем undefined/null
    state?: Record<string, unknown>;
};

export const VideoItem: React.FC<Props> = ({ item, state }) => {
    // защита от кривых данных
    if (!item || item.id == null) return null;

    const {
        id,
        slug,
        title,
        subtitle,
        thumb = '',
        upcoming = false,
        releasedTs,        // публикация (ms)
        releaseTs,         // премьера (ms)
    } = item;

    const targetPath = `/videos/${slug ?? id}`;

    const thumbEl = (
        <Image
            src={thumb || ''}
            alt={title || 'Video preview'}
            w="100%"
            radius="md"
            fit="cover"
            loading="lazy"
        />
    );

    const countdownRender = ({ days, hours, minutes, seconds, completed }: CountdownRenderProps) => {
        if (completed) return <span>Released</span>;
        if (days && days > 0) return <span>{days} days {hours} hours</span>;
        if ((days ?? 0) === 0 && hours && hours > 0) return <span>{hours} hours {minutes} minutes</span>;
        return <span>{minutes}:{String(seconds).padStart(2, '0')}</span>;
    };

    const picture = (
        <div className={classes.thumbnail}>
            {thumb ? (
                upcoming ? (
                    thumbEl
                ) : (
                    <Link
                        to={targetPath}
                        title={title || subtitle || 'Open video'}
                        state={state ?? { id, slug, title, subtitle, thumb, upcoming, releasedTs, releaseTs }}
                        aria-label={title || subtitle || 'Open video'}
                    >
                        {thumbEl}
                    </Link>
                )
            ) : (
                <div className={classes.thumbnailPlaceholder} />
            )}

            {upcoming && typeof releaseTs === 'number' && (
                <Badge className={classes.upcoming} radius={0} fw={400} variant="filled">
                    <Countdown date={releaseTs} renderer={countdownRender} />
                </Badge>
            )}
        </div>
    );

    const titleContent = (
        <>
            {title}
            {title && subtitle ? ' ' : ''}
            {subtitle}
        </>
    );

    const titleEl = upcoming ? (
        <Text className={classes.title}>{titleContent}</Text>
    ) : (
        <Anchor
            component={Link}
            className={classes.title}
            to={targetPath}
            title={title || subtitle || 'Open video'}
            state={state ?? { id, slug, title, subtitle, thumb, upcoming, releasedTs, releaseTs }}
        >
            {titleContent}
        </Anchor>
    );

    const metaTs =
        typeof releasedTs === 'number'
            ? releasedTs
            : typeof releaseTs === 'number' && releaseTs <= Date.now()
                ? releaseTs
                : undefined;

    return (
        <Card radius={0} className={classes.video__item} p={0} bg="transparent"   withBorder={false}>
            <Stack gap={8}>
                {picture}
                {titleEl}
                {typeof metaTs === 'number' && (
                    <div className={classes.meta}>{timeAgo(metaTs)}</div>
                )}
            </Stack>
        </Card>
    );
};
